import { Loader2, Trash, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { type AuthoringStatus, authoringStatusMeta, STATUS_OPTIONS } from './StatusPill';

/**
 * The bulk-action bar for a list with row selection — Set status / Delete / Clear.
 *
 * FLOATS UP FROM THE BOTTOM, which is Studio's own pattern (lifted from the workflows list) and
 * deliberately NOT the reference's, where the same buttons sit in a strip above the table header. Two
 * reasons the bottom is better here and why it is worth diverging:
 *
 *   • The strip above the header PUSHES THE TABLE DOWN the moment a checkbox is ticked, so the row you
 *     just clicked moves out from under the cursor and the next tick lands on the wrong row.
 *   • It scrolls away with the header. Selecting rows near the bottom of a long list then leaves the
 *     only way to act on them off screen.
 *
 * The bar is `fixed`, so neither happens: the table never shifts and the actions are reachable
 * wherever you are in the list.
 *
 * It renders NOTHING when nothing is selected, so a list without selection is unchanged.
 */
export function BulkActionBar({
  selectedCount,
  noun,
  busy,
  onSetStatus,
  onDelete,
  onClear,
}: {
  selectedCount: number;
  /** Singular noun for the count — "workflow", "questionnaire". Pluralised here. */
  noun: string;
  busy?: boolean;
  /** Omit to hide the status buttons — for a list whose rows have no status. */
  onSetStatus?: (target: AuthoringStatus) => void;
  onDelete?: () => void;
  onClear: () => void;
}) {
  if (selectedCount === 0) {
    return null;
  }

  return (
    // `pointer-events-none` on the wrapper so the full-width strip does not swallow clicks on the
    // page behind it; the bar itself turns them back on.
    <div className="pointer-events-none fixed inset-x-0 bottom-6 z-40 flex justify-center px-4">
      <div className="pointer-events-auto flex flex-wrap items-center gap-2.5 rounded-full border border-grayEF bg-[#F4F6FD] px-4 py-2.5 shadow-elevation-2 duration-200 animate-in slide-in-from-bottom-4">
        <span className="text-[length:var(--text-14)] font-semibold text-black-logo">
          {selectedCount} selected
        </span>

        {onSetStatus && (
          <>
            <span className="h-5 w-px bg-grayEF" />
            <span className="text-[length:var(--text-12)] text-gray66">Set status</span>
            {STATUS_OPTIONS.map((target) => {
              const meta = authoringStatusMeta(target);
              const { Icon } = meta;
              return (
                <button
                  key={target}
                  type="button"
                  onClick={() => onSetStatus(target)}
                  disabled={busy}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[length:var(--text-13)] font-medium transition hover:brightness-95 disabled:opacity-50',
                    // The status's own tint — the same pairs the pill and its menu use, so the three
                    // places a status appears cannot drift.
                    meta.selected,
                  )}
                >
                  <Icon className="h-3.5 w-3.5" /> {meta.label}
                </button>
              );
            })}
          </>
        )}

        {busy && <Loader2 className="h-4 w-4 animate-spin text-primary" />}

        {onDelete && (
          <>
            <span className="h-5 w-px bg-grayEF" />
            <button
              type="button"
              onClick={onDelete}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#FEECEC] px-3 py-1.5 text-[length:var(--text-13)] font-medium text-redBF transition hover:brightness-95 disabled:opacity-50"
            >
              <Trash className="h-3.5 w-3.5" /> Delete
            </button>
          </>
        )}

        <span className="h-5 w-px bg-grayEF" />
        <button
          type="button"
          onClick={onClear}
          aria-label={`Clear selection of ${selectedCount} ${noun}${selectedCount === 1 ? '' : 's'}`}
          className="inline-flex h-7 w-7 items-center justify-center rounded-full text-gray66 transition hover:bg-grayEF"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
